/**
 * Redux slice for the Work Item Detail Page.
 *
 * Owns two `createAsyncThunk`s — `loadWorkItem` and `resolveWorkItem` — plus
 * the lifecycle state that drives the detail page's mount/render/resolve
 * flow.
 *
 * Invariants upheld by the reducer:
 *   (a) loadStatus === FULFILLED  ⇒  workItem !== null
 *                                 ∧  loadedWorkItemId === workItem.workItemId
 *   (b) loadStatus === REJECTED   ⇒  workItem === null ∧ loadError !== null
 *   (c) any loadWorkItem.pending action resets resolveStatus to IDLE and
 *       resolveError to null (fresh load clears the resolve lifecycle so a
 *       stale FULFILLED cannot auto-navigate when the user returns to the
 *       page for a new id)
 *   (d) resolveStatus === FULFILLED ⇒ prior loadStatus === FULFILLED
 *
 * Error-handling contract:
 *   - loadWorkItem thunk dispatches `showError({ title: "Failed to Load Work
 *     Item", ... })` before `rejectWithValue`.
 *   - resolveWorkItem thunk dispatches `showError({ title: "Failed to Resolve
 *     Task", ... })` before `rejectWithValue`.
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { showError } from "../../store/slices/errorSlice";
import { WorkItemDetailStatus, WorkItemStatus } from "../../utils/constants";
import { fetchLoadWorkItem, fetchUpdateWorkItemState } from "./api";
import {
  initialState,
  LoadWorkItemRequest,
  LoadWorkItemResponse,
  UpdateWorkItemStateResponse,
  WorkItemDetailState,
} from "./model";

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const loadWorkItem = createAsyncThunk<
  LoadWorkItemResponse,
  LoadWorkItemRequest,
  { rejectValue: string }
>(
  "workItemDetail/loadWorkItem",
  async (req, { rejectWithValue, dispatch }) => {
    try {
      return await fetchLoadWorkItem(req);
    } catch (err: any) {
      dispatch(
        showError({
          title: "Failed to Load Work Item",
          message: err?.message ?? "Unable to load work item. Please try again.",
        }),
      );
      return rejectWithValue(err?.message ?? "Load failed");
    }
  },
);

export const resolveWorkItem = createAsyncThunk<
  UpdateWorkItemStateResponse,
  { workItemId: string; operator: string },
  { rejectValue: string }
>(
  "workItemDetail/resolveWorkItem",
  async ({ workItemId, operator }, { rejectWithValue, dispatch }) => {
    try {
      return await fetchUpdateWorkItemState({
        workItemId,
        status: WorkItemStatus.RESOLVED,
        operator,
      });
    } catch (err: any) {
      dispatch(
        showError({
          title: "Failed to Resolve Task",
          message:
            err?.message ?? "Unable to resolve work item. Please try again.",
        }),
      );
      return rejectWithValue(err?.message ?? "Resolve failed");
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const workItemDetailSlice = createSlice({
  name: "workItemDetail",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ─── loadWorkItem ───────────────────────────────────────────────────
      .addCase(loadWorkItem.pending, (state) => {
        state.loadStatus = WorkItemDetailStatus.LOADING;
        state.loadError = null;
        // Invariant (c): fresh load resets the resolve lifecycle so a stale
        // FULFILLED cannot auto-navigate when the user returns to the page
        // for a new id.
        state.resolveStatus = WorkItemDetailStatus.IDLE;
        state.resolveError = null;
      })
      .addCase(
        loadWorkItem.fulfilled,
        (state, action: PayloadAction<LoadWorkItemResponse>) => {
          // Invariant (a): workItem !== null and the id stays in sync.
          state.loadStatus = WorkItemDetailStatus.FULFILLED;
          state.workItem = action.payload.workItem;
          state.loadedWorkItemId = action.payload.workItem.workItemId;
          state.loadError = null;
        },
      )
      .addCase(loadWorkItem.rejected, (state, action) => {
        // Invariant (b): loadError is never null on rejection; stale data
        // is cleared so the UI cannot render a previously-loaded work item.
        state.loadStatus = WorkItemDetailStatus.REJECTED;
        state.workItem = null;
        state.loadedWorkItemId = null;
        state.loadError =
          action.payload ?? action.error.message ?? "Load failed";
      })
      // ─── resolveWorkItem ───────────────────────────────────────────────
      .addCase(resolveWorkItem.pending, (state) => {
        state.resolveStatus = WorkItemDetailStatus.LOADING;
        state.resolveError = null;
      })
      .addCase(resolveWorkItem.fulfilled, (state) => {
        // Invariant (d) is upheld structurally: the button that dispatches
        // this thunk is only mounted once loadStatus === FULFILLED, and
        // nothing here demotes loadStatus.
        state.resolveStatus = WorkItemDetailStatus.FULFILLED;
        state.resolveError = null;
      })
      .addCase(resolveWorkItem.rejected, (state, action) => {
        // On rejection we reset resolveStatus back to IDLE so the button
        // re-enables and the user can retry immediately. Capture the error
        // message in resolveError.
        state.resolveStatus = WorkItemDetailStatus.IDLE;
        state.resolveError =
          action.payload ?? action.error.message ?? "Resolve failed";
      });
  },
});

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectWorkItem = (state: {
  workItemDetail: WorkItemDetailState;
}) => state.workItemDetail.workItem;

export const selectLoadStatus = (state: {
  workItemDetail: WorkItemDetailState;
}) => state.workItemDetail.loadStatus;

export const selectLoadError = (state: {
  workItemDetail: WorkItemDetailState;
}) => state.workItemDetail.loadError;

export const selectResolveStatus = (state: {
  workItemDetail: WorkItemDetailState;
}) => state.workItemDetail.resolveStatus;

export const selectResolveError = (state: {
  workItemDetail: WorkItemDetailState;
}) => state.workItemDetail.resolveError;

export const selectLoadedWorkItemId = (state: {
  workItemDetail: WorkItemDetailState;
}) => state.workItemDetail.loadedWorkItemId;

// ---------------------------------------------------------------------------
// Reducer export
// ---------------------------------------------------------------------------

export default workItemDetailSlice.reducer;
